import {
  sideMenuBuilder,
  sideMenuProfilesBuilder,
} from "components/menu/side/Builder";
import { TopMenuActions, topMenuBuider } from "components/menu/top/Builder";
import { showDoubleActionPopUp } from "components/popup/doubleAction";
import { showToast } from "components/toast";
import { useCaixaRole } from "contexts/CaixaRoleContext";
import { updateProfileImageLink } from "domain/feature/hub/user-config/PersonalConfigForm/store/profileImage";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router";
import { useSmartNavigateLegacy } from "components/SmartLink";
import {
  api,
  DeleteFormat,
  deleteProvider,
  getProvider,
  updateProvider,
} from "sharedKernel/api";
import { logout, setAuth, setPermissions } from "sharedKernel/auth";
import { breadCrumbBuilder } from "sharedKernel/breadCrumb/Builder";
import { ROUTES } from "sharedKernel/constants";
import { cookies, setCookie, removeCookie } from "sharedKernel/cookies/behavior";
import { removeCredLocalStorage } from "sharedKernel/auth/credLocalStorage";
import { getStartRoute, getHubRoute } from "sharedKernel/handlers";
import { regexPatterns } from "sharedKernel/regex";
import { caixaUser } from "sharedKernel/user/CaixaUser";
import { loggedUser } from "sharedKernel/user/LoggedUser";
import { changeSidebarState, store, TReducers } from "store";

const MOBILE_BREAKPOINT_PX = 576;
const SMART_NAV_ENABLED = process.env.REACT_APP_SMART_NAV_ENABLED === 'true';
const SHELL_URL = process.env.REACT_APP_SHELL_URL ?? 'http://localhost:3000';

// The impersonation state has to survive the app-* -> legacy-* hop. It was React state in
// each app, so the legacy always booted with isBehavingAs=false: it hid the "back to company
// selection" button and the menu, and showed the company search instead. Persist it in a
// cookie on the shared parent domain — the same mechanism the session itself already uses.
const BEHAVE_AS_COOKIE = "_behaveAsCompany";

export const useInnerPageHooks = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isDarkModeOn, setDarkMode] = useState(false);

  useLayoutEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches
    ) {
      setIsSidebarOpen(false);
    }
  }, []);

  const [filterText, setFilterText] = useState<string>("");
  const [companies, setCompanies] = useState<any[] | null>([]);
  const { mutate: getMutate } = getProvider();
  const { mutate: updateMutate } = updateProvider();
  const [isBehavingAs, setBehavingAs] = useState<boolean>(
    () => cookies.get(BEHAVE_AS_COOKIE) !== undefined
  );
  const [forceRenderKey, setForceRenderKey] = useState<number>(0);
  const [user] = useState(loggedUser.get());
  const [isSuperAdmin] = useState<boolean>(user.isSuperAdmin);
  const userAuth = useSelector((state: TReducers) => state.auth);

  const filteredCompanies = useMemo(() => {
    if (!filterText.trim()) {
      return companies;
    }

    return companies?.filter((company) =>
      (company.name ?? company.title ?? "")
        .toLowerCase()
        .includes(filterText.toLowerCase().trim())
    );
  }, [companies, filterText]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  const isDarkMode = useSelector(
    (state: TReducers) => state.isDarkModeOn.isDarkModeOn
  );
  const featureType = useSelector((state: TReducers) =>
    state.auth.permissions?.features?.map((feature) => feature.code)
  );
  const { selectedRole, isCaixaUser, setSelectedRole } = useCaixaRole();

  const onSuccessDeletion = async (): Promise<void> => {

    console.log("entrou aqui no onSuccessDeletion");

    setCookie("_banner_update_late", false);
    setCookie("_userUpdatedAt", null);

    console.log("SMART_NAV_ENABLED " + SMART_NAV_ENABLED);

    if (SMART_NAV_ENABLED) {
      removeCookie('_cred');
      removeCookie('_perm');
      removeCookie('_permCodes');
      removeCookie('_usrData');
      removeCookie('_profImg');
      removeCookie('_behaveAsCompany');
      // This branch hard-redirects to the shell instead of dispatching LOGOUT, so it has
      // to clear the profile payload itself — otherwise it survives into the next session.
      removeCredLocalStorage();

      console.log("Redirect URL " + SHELL_URL + ROUTES.USER_AUTH.LOGIN);

      window.location.replace(SHELL_URL + ROUTES.USER_AUTH.LOGIN);
    } else {
      dispatch(logout());
      navigate(ROUTES.USER_AUTH.LOGIN);
    }
  };

  const navigate = useSmartNavigateLegacy();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const { mutate: deleteAuth } = deleteProvider(onSuccessDeletion);
  const { mutate: deleteMutate } = deleteProvider();

  const handleDeleteAuth = (): void => {
    const request: DeleteFormat = {
      url: "/v1/auth/login",
    };
    deleteAuth(request);
  };
  const handleLogout = (): void => {
    showDoubleActionPopUp({
      title: "Logout",
      message: "Deseja realmente se desconectar?",
      buttons: [{ text: "Sim" }, { text: "Não" }],
      action: async () => handleDeleteAuth(),
    });
  };
  const topMenuActions: TopMenuActions = {
    logoutAction: handleLogout,
  };
  // const user = loggedUser.get();
  const selectedProfileName =
    (cookies.get("_selectedProfileName") as string) || "";
  const menuTopItems = topMenuBuider.build(topMenuActions, user);

  // Para usuários da Caixa, garantir que tenham as permissões corretas
  let updatedFeatureType = featureType;
  if (caixaUser.isCaixaUser() && (!featureType || featureType.length === 0)) {
    updatedFeatureType = ["caixa_employee", "idea", "evaluation"];
  }
  const profileItems = sideMenuProfilesBuilder.build(
    location.pathname,
    featureType
  );
  const menuItems = sideMenuBuilder.build(
    location.pathname,
    updatedFeatureType,
    isCaixaUser ? selectedRole : undefined
  );

  const breadcrumb = breadCrumbBuilder.build();

  useEffect(() => {
    dispatch(changeSidebarState(isSidebarOpen));
  }, [isSidebarOpen, dispatch]);

  const handleCompanySelect = useCallback(
    (menuItem) => {
      if (menuItem.id && !menuItem.path) {
        updateMutate(
          {
            url: `/v1/manager/access/behavior/behave-as/company/${menuItem.id}`,
          },
          {
            onSuccess: () => {
              queueMicrotask(() => {
                setCookie(BEHAVE_AS_COOKIE, menuItem.id);
                setBehavingAs(true);
                setForceRenderKey((prev) => prev + 1); // Force re-render
                setBehaveAs(user);

                // `/hub/dashboard`, not a bare "dashboard". useSmartNavigate passes the string through
                // as-is, and an unprefixed path matches no shell route, so smartNav falls through to the
                // legacy host and lands on /dashboard — which is not a route on either app (404).
                navigate(getHubRoute(ROUTES.HUB.DASHBOARD));
              });
            },
            onError: (error) => {
              queueMicrotask(() => {
                removeCookie(BEHAVE_AS_COOKIE);
                setBehavingAs(false);
                setForceRenderKey((prev) => prev + 1);
                console.error("Error switching to company:", error);
              });
            },
          }
        );
      }
    },
    [updateMutate, user]
  );

  const handleBackToCompanySelection = useCallback(() => {
    deleteMutate(
      {
        url: "/v1/manager/access/behavior/behave-as/company",
      },
      {
        onSuccess: (success) => {
          if (success.status === 200) {
            queueMicrotask(() => {
              removeCookie(BEHAVE_AS_COOKIE);
              setBehavingAs(false);
              setFilterText("");
              setForceRenderKey((prev) => prev + 1);

              // `/hub/dashboard`, not a bare "dashboard". useSmartNavigate passes the string through
              // as-is, and an unprefixed path matches no shell route, so smartNav falls through to the
              // legacy host and lands on /dashboard — which is not a route on either app (404).
              navigate(getHubRoute(ROUTES.HUB.DASHBOARD));
            });
          }
        },
        onError: (error) => {
          console.error("Error switching back to company selection:", error);
        },
      }
    );
  }, [deleteMutate]);

  const handleGetCompanyData = useCallback(async () => {
    try {
      getMutate(
        { url: "/v1/manager/company" },
        {
          onSuccess: (response) => {
            if (response?.data.rows) {
              const companies = response.data.rows.map((company: any) => {
                return {
                  title: company?.contactName || company?.name,
                  id: company?.id,
                  name: company?.contactName || company?.name,
                  path: undefined,
                };
              });
              setCompanies(companies);
            }
          },
          onError: (error) => {
            console.error("Erro ao obter dados da empresa:", error);
          },
        }
      );
    } catch (error) {
      console.error("Erro ao obter dados da empresa:", error);
    }
  }, [getMutate]);

  useEffect(() => {
    if (user.isSuperAdmin) {
      handleGetCompanyData();
    }
  }, [user.isSuperAdmin]);

  const isFormPage = regexPatterns.isFormPage.test(location.pathname);

  const setBehaveAs = async (data) => {
    try {
      if (!data) {
        console.error("Dados de companhia inválidos:", data);
        showToast("error", "Erro interno: dados de companhia inválidos");
        return;
      }

      let isCaixaEmployee = false;
      try {
        const tokenPayload = JSON.parse(atob(data.data.token.split(".")[1]));
        isCaixaEmployee = tokenPayload.role === "CaixaEmployee";
      } catch (error) {
        isCaixaEmployee = data.email?.includes("@caixa") ?? false;
      }

      if (data.id) {
        if (isCaixaEmployee) {
          setUserProfileImage(data.id, isCaixaEmployee).then(() => {
            setUserPermissions(isCaixaEmployee);
          });
        } else {
          setUserProfileImage(data.id, isCaixaEmployee);
          setUserPermissions(isCaixaEmployee);
        }
      } else {
        setUserPermissions(isCaixaEmployee);
      }
    } catch (error) {
      console.error("Erro ao configurar login:", error);
      showToast("error", "Erro interno ao configurar login");
    }
  };

  const setUserPermissions = async (
    isCaixaEmployee: boolean = false
  ): Promise<void> => {
    try {
      if (isCaixaEmployee) {
        const caixaPermissions = {
          id: "00000000-0000-0000-0000-000000000000",
          name: "CaixaEmployee",
          features: [
            {
              id: "00000000-0000-0000-0000-000000000001",
              code: "caixa_employee",
              access: [
                { id: "00000000-0000-0000-0000-000000000001", code: "view" },
                { id: "00000000-0000-0000-0000-000000000002", code: "edit" },
                { id: "00000000-0000-0000-0000-000000000003", code: "launch" },
              ],
            },
            {
              id: "00000000-0000-0000-0000-000000000002",
              code: "idea",
              access: [
                { id: "00000000-0000-0000-0000-000000000004", code: "view" },
                { id: "00000000-0000-0000-0000-000000000005", code: "edit" },
                { id: "00000000-0000-0000-0000-000000000006", code: "launch" },
              ],
            },
            {
              id: "00000000-0000-0000-0000-000000000003",
              code: "evaluation",
              access: [
                { id: "00000000-0000-0000-0000-000000000007", code: "view" },
                { id: "00000000-0000-0000-0000-000000000008", code: "edit" },
                { id: "00000000-0000-0000-0000-000000000009", code: "launch" },
              ],
            },
          ],
        };

        dispatch(setPermissions(caixaPermissions));
        const permissions = ["caixa_employee", "idea", "evaluation"];

        const startRoute = getStartRoute(permissions);

        return;
      }

      const response = await api.get("/v1/system/user/my-permissions");

      if (response.status === 200) {
        let permissionsData = response?.data?.data;

        dispatch(setPermissions(permissionsData));
        const permissions: string[] = [];
        permissionsData.features?.map((feature, index): any => {
          permissions.push(String(feature.code));
          return feature;
        });

        const startRoute = getStartRoute(permissions);
      } else {
        throw new Error(`Status inválido: ${response.status}`);
      }
    } catch (error) {
      if (isCaixaEmployee) {
        const mockPermissions = {
          id: "00000000-0000-0000-0000-000000000000",
          name: "CaixaEmployee",
          features: [
            {
              id: "00000000-0000-0000-0000-000000000001",
              code: "caixa_employee",
              access: [
                { id: "00000000-0000-0000-0000-000000000001", code: "view" },
                { id: "00000000-0000-0000-0000-000000000002", code: "edit" },
                { id: "00000000-0000-0000-0000-000000000003", code: "launch" },
              ],
            },
            {
              id: "00000000-0000-0000-0000-000000000002",
              code: "idea",
              access: [
                { id: "00000000-0000-0000-0000-000000000004", code: "view" },
                { id: "00000000-0000-0000-0000-000000000005", code: "edit" },
                { id: "00000000-0000-0000-0000-000000000006", code: "launch" },
              ],
            },
            {
              id: "00000000-0000-0000-0000-000000000003",
              code: "evaluation",
              access: [
                { id: "00000000-0000-0000-0000-000000000007", code: "view" },
                { id: "00000000-0000-0000-0000-000000000008", code: "edit" },
                { id: "00000000-0000-0000-0000-000000000009", code: "launch" },
              ],
            },
          ],
        };
        dispatch(setPermissions(mockPermissions));
        navigate("/hub/startup/challenge", { replace: true });
      } else {
        showToast(
          "error",
          "Erro ao carregar permissões. Tente fazer login novamente."
        );
        console.error(
          "Erro fatal: não foi possível carregar permissões para usuário não-Caixa"
        );
      }
    }
  };

  const setUserProfileImage = async (
    userId: string,
    isCaixaEmployee: boolean = false
  ): Promise<void> => {
    try {
      const noImage =
        "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460__340.png";

      const endpoint = isCaixaEmployee
        ? `/v1/system/caixa-employee`
        : `/v1/system/user/${userId}`;

      const response = await api.get(endpoint);
      if (response.status === 200) {
        let profileImageLink = noImage;

        if (isCaixaEmployee) {
          profileImageLink =
            response.data.data?.profileImage ||
            response.data.data?.avatar ||
            response.data.data?.profileImageUrl ||
            noImage;

          const userData = response.data.data;
          if (userData) {
            const completeUserData = {
              id: userId,
              data: {
                ...userData,
                token: store.getState().auth.data?.token, // Manter o token atual
                temporaryToken: store.getState().auth.data?.temporaryToken, // Manter o temporaryToken
                name:
                  userData.name ||
                  userData.userName ||
                  userData.fullName ||
                  "Usuário",
                email: userData.email || userData.userEmail || "",
                role: {
                  id: userData.roleId || "00000000-0000-0000-0000-000000000000",
                  name: userData.role || "CaixaEmployee",
                },
                company: {
                  id:
                    userData.companyId ||
                    "00000000-0000-0000-0000-000000000000",
                  name: userData.companyName || "Caixa Econômica Federal",
                },
              },
              isAuthenticated: true,
            };

            store.dispatch(setAuth(completeUserData));
          }
        } else {
          profileImageLink = response.data.data?.profileImage || noImage;
        }

        store.dispatch(updateProfileImageLink(profileImageLink));
      } else {
        store.dispatch(updateProfileImageLink(noImage));
      }
    } catch (error) {
      const noImage =
        "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460__340.png";
      store.dispatch(updateProfileImageLink(noImage));
    }
  };

  // On hard reload Redux is empty even though the user is authenticated via cookie.
  // Re-fetch permissions so the menu builds correctly without requiring a new login.
  useEffect(() => {
    if (userAuth.isAuthenticated && (!featureType || featureType.length === 0)) {
      setUserPermissions(caixaUser.isCaixaUser());
    }
  }, []);

  return {
    isSidebarOpen,
    setIsSidebarOpen,
    isDarkModeOn,
    setDarkMode,
    filteredCompanies,
    filterText,
    handleFilterChange,
    isBehavingAs,
    isSuperAdmin,
    user,
    menuTopItems,
    profileItems,
    menuItems,
    breadcrumb,
    isFormPage,
    handleCompanySelect,
    handleBackToCompanySelection,
    selectedProfileName,
    isCaixaUser,
    setSelectedRole,
    selectedRole,
    forceRenderKey, // Export this to use as a key prop
  };
};
